/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 search box for list and board toolbars (design-reference table toolbar):
 * 36px, lucide Search icon, and an X that clears it. Controlled.
 *
 * @param {object} props
 * @param {string} props.value Current text; null/undefined show as empty.
 * @param {(value: string, event?: Event) => void} props.onChange Called on every keystroke, and with "" on clear.
 * @param {string} [props.placeholder="Search"] Also the accessible name unless `ariaLabel` is given.
 * @param {string} [props.ariaLabel]
 * @param {() => void} [props.onClear] Called after onChange("") when the X is pressed.
 * @param {number|string|object} [props.width=260] Any MUI sx width, responsive objects included.
 * @param {"medium"|"small"} [props.size="medium"] 36px or 30px.
 * @param {boolean} [props.autoFocus=false]
 * @param {object} [props.inputProps] Passed to the <input>.
 * @param {object} [props.sx]
 */

import { useRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputBase from "@mui/material/InputBase";
import { Search, X } from "lucide-react";
import { v2 as t, v2Density as d, V2_FONT_FAMILY } from "styles/tokens/v2";

export default function FilterSearch({ value, onChange, placeholder, ariaLabel, onClear, width, size, autoFocus, inputProps, sx }) {
  const inputRef = useRef(null);
  const text = value === null || value === undefined ? "" : String(value);
  const small = size === "small";

  const clear = () => {
    onChange("");
    if (onClear) onClear();
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: small ? d.controlHeightSmallPx : d.controlHeightPx,
        width,
        maxWidth: "100%",
        px: small ? 1.25 : 1.5,
        borderRadius: `${d.radiusPx}px`,
        border: `1px solid ${t.border2}`,
        bgcolor: t.surface,
        color: t.text3,
        transition: "border-color 120ms",
        "&:hover": { borderColor: t.text3 },
        "&:focus-within": { borderColor: t.primary, outline: `2px solid ${t.link}`, outlineOffset: 1 },
        ...sx,
      }}
    >
      <Search size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
      <InputBase
        inputRef={inputRef}
        value={text}
        onChange={(e) => onChange(e.target.value, e)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        inputProps={{ "aria-label": ariaLabel || placeholder, type: "search", ...inputProps }}
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: V2_FONT_FAMILY,
          fontSize: small ? 13 : 14,
          color: t.text,
          "& input": { p: 0, height: "100%" },
          "& input::placeholder": { color: t.text3, opacity: 1 },
          "& input::-webkit-search-cancel-button": { display: "none" },
        }}
      />
      {text && (
        <ButtonBase
          type="button"
          aria-label="Clear search"
          onClick={clear}
          sx={{
            width: 22,
            height: 22,
            borderRadius: "6px",
            color: t.text3,
            flexShrink: 0,
            "&:hover": { bgcolor: t.hover, color: t.text },
            "&.Mui-focusVisible": { outline: `2px solid ${t.link}` },
          }}
        >
          <X size={14} aria-hidden="true" />
        </ButtonBase>
      )}
    </Box>
  );
}

FilterSearch.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClear: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object]),
  size: PropTypes.oneOf(["medium", "small"]),
  autoFocus: PropTypes.bool,
  inputProps: PropTypes.object,
  sx: PropTypes.object,
};

FilterSearch.defaultProps = {
  value: "",
  placeholder: "Search",
  ariaLabel: undefined,
  onClear: undefined,
  width: 260,
  size: "medium",
  autoFocus: false,
  inputProps: {},
  sx: {},
};
